import axios, { AxiosRequestHeaders } from "axios";
import { useToast } from "../hooks/useToast";
import { useLoader } from "../hooks/useLoader";
import { useEffect } from "react";
import { config } from "../utils/config";
import { useMsal } from "@azure/msal-react";
import { getLoginRequest } from "../authConfig";
import { IPublicClientApplication } from "@azure/msal-browser";

// Función para notificar al contexto de roles que se ha renovado el token
const notifyRolesContext = () => {
  // Disparar evento personalizado para que el contexto se actualice
  window.dispatchEvent(new CustomEvent('tokenRenewed'));
};

// Cache de token con verificación de expiración
interface TokenCache {
  token: string | null;
  expiresOn: Date | null;
  lastUpdated: Date;
}

let tokenCache: TokenCache = {
  token: null,
  expiresOn: null,
  lastUpdated: new Date(0)
};

// Función para verificar si el token está próximo a expirar (5 minutos de margen)
const isTokenExpiringSoon = (expiresOn: Date | null): boolean => {
  if (!expiresOn) return true;

  const now = new Date();
  const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

  return expiresOn <= fiveMinutesFromNow;
};

// Función para obtener token con cache inteligente
const getCachedToken = async (instance: IPublicClientApplication): Promise<string | null> => {
  const activeAccount = instance.getActiveAccount();
  if (!activeAccount) return null;

  // Si el token está próximo a expirar o no existe, renovarlo
  if (!tokenCache.token || isTokenExpiringSoon(tokenCache.expiresOn)) {
    try {
      const tokenRequest = getLoginRequest();
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: activeAccount,
      });

      if (response.accessToken) {
        tokenCache = {
          token: response.accessToken,
          expiresOn: response.expiresOn,
          lastUpdated: new Date()
        };

        console.log('🔄 Token renovado:', {
          expiresOn: formatExpirationDate(response.expiresOn),
          timeRemaining: getTokenTimeRemaining(response.expiresOn)
        });
      }
    } catch (error) {
      console.warn('Error al renovar token:', error);
      // Limpiar cache en caso de error
      tokenCache = {
        token: null,
        expiresOn: null,
        lastUpdated: new Date()
      };
      return null;
    }
  }

  return tokenCache.token;
};

// Función para calcular el tiempo restante del token
const getTokenTimeRemaining = (expiresOn: Date | null): string => {
  if (!expiresOn) {
    return 'No disponible';
  }

  const now = Date.now();
  const expirationTime = expiresOn.getTime();
  const timeRemaining = expirationTime - now;

  if (timeRemaining <= 0) {
    return 'Token expirado';
  }

  const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// Función para formatear la fecha de expiración
const formatExpirationDate = (expiresOn: Date | null): string => {
  if (!expiresOn) {
    return 'No disponible';
  }

  return expiresOn.toLocaleString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/Santiago'
  });
};

// Función auxiliar para obtener la URL base según configuración
const getBaseUrl = (): string => {
  return config.USE_AZURE_APIM
    ? config.URL_AZURE_APIM_API_SISCRED
    : config.URL_API_SISCRED;
};

const axiosInstance = axios.create({
  baseURL: (() => {

    const waitForConfig = () => {
      return new Promise<string>((resolve) => {

        if (config.URL_API_SISCRED !== '' && config.URL_AZURE_APIM_API_SISCRED !== '') {
          resolve(getBaseUrl());
        } else {

          const checkInterval = setInterval(() => {
            if (config.URL_API_SISCRED !== '' && config.URL_AZURE_APIM_API_SISCRED !== '') {
              clearInterval(checkInterval);
              resolve(getBaseUrl());
            }
          }, 100);

          // Timeout de seguridad después de 10 segundos
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve(getBaseUrl());
          }, 10000);
        }
      });
    };

    // Inicializar con un valor vacío y actualizar cuando esté disponible
    let baseUrl = '';
    waitForConfig().then(url => {
      const normalizedBaseUrl = url;
      baseUrl = normalizedBaseUrl;
      axiosInstance.defaults.baseURL = normalizedBaseUrl;
      console.log("Base URL de Axios actualizada a:", normalizedBaseUrl);
    });

    return baseUrl;
  })(),
  headers: (() => {
    const waitForApimKey = () => {
      return new Promise<string>((resolve) => {
        if (config.OCP_APIM_KEY !== '') {
          resolve(config.OCP_APIM_KEY);
        } else {
          const checkInterval = setInterval(() => {
            if (config.OCP_APIM_KEY !== '') {
              clearInterval(checkInterval);
              resolve(config.OCP_APIM_KEY);
            }
          }, 100);

          // Timeout de seguridad después de 10 segundos
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve(config.OCP_APIM_KEY);
          }, 10000);
        }
      });
    };

    // Inicializar con un valor vacío y actualizar cuando esté disponible
    let apimKey = '';
    waitForApimKey().then(key => {
      apimKey = key;
      axiosInstance.defaults.headers['Ocp-Apim-Subscription-Key'] = key;
    });

    return {
      'Ocp-Apim-Subscription-Key': apimKey
    };
  })(),
});

const AxiosInterceptor: React.FC = () => {
  const { addToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const { instance } = useMsal();

  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      async (request) => {
        if (request.baseURL) {
          request.baseURL = request.baseURL;
        }

        if (request.url) {
          request.url = request.url;
        }

        // Obtener token de acceso usando cache inteligente
        try {
          console.log('🔍 Verificando token antes de la petición...');
          const token = await getCachedToken(instance);

          if (token) {
            console.log('✅ Token válido agregado a la petición.');
            request.headers.Authorization = `Bearer ${token}`;
            request.headers.Token = `Bearer ${token}`;
          }
          else {
            console.warn('⚠️ No se obtuvo un token válido.');
          }
        } catch (error) {
          console.warn('No se pudo obtener el token de acceso:', error);
          // No agregar el header de autorización si no se puede obtener el token
        }

        // Esto es para ignorar el loader en peticiones específicas 
        // { headers: { 'X-Hide-Loader': 'true' } } ejemplo cargarlo a headers de axios
        const isLoader = request.headers?.["X-Hide-Loader"] !== "true";
        if (isLoader) {
          showLoader();
        }

        return request;
      },
      (error) => {
        hideLoader();
        addToast({
          mostrar: true,
          titulo: "Error",
          tipo: "Danger",
          mensaje: "Ha ocurrido un error",
        });
        return error;
      }
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => {
        hideLoader();
        return response;
      },
      async (error) => {
        hideLoader();

        // Manejar error 401 (No autorizado) - Token expirado
        if (error?.response?.status === 401) {
          console.warn('Token expirado o no válido, redirigiendo al login...');

          // Limpiar cache de token
          tokenCache = {
            token: null,
            expiresOn: null,
            lastUpdated: new Date(0)
          };

          // Mostrar información del token que expiró
          const activeAccount = instance.getActiveAccount();
          if (activeAccount && activeAccount.idTokenClaims) {
            console.log('Información del token expirado:', {
              username: activeAccount.username,
              expiresOn: activeAccount.idTokenClaims.exp,
              expiresOnDate: activeAccount.idTokenClaims.exp ? new Date(activeAccount.idTokenClaims.exp * 1000).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : 'No disponible',
              issuedAt: activeAccount.idTokenClaims.iat,
              issuedAtDate: activeAccount.idTokenClaims.iat ? new Date(activeAccount.idTokenClaims.iat * 1000).toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : 'No disponible'
            });
          }

          // Limpiar cuenta activa
          instance.setActiveAccount(null);

          // Redirigir al login con scopes dinámicos
          try {
            const tokenRequest = getLoginRequest();
            await instance.loginPopup({
              ...tokenRequest,
              redirectUri: '/',
            });
          } catch (loginError) {
            console.error('Error al redirigir al login:', loginError);
          }

          return Promise.reject(error);
        }

        const defaultMessage = "Ha ocurrido un error";
        let message = defaultMessage;

        try {
          // Detectar si el error viene como blob con contenido JSON
          const contentType = error?.response?.headers?.['content-type'];
          const isBlob = error?.response?.data instanceof Blob;

          if (isBlob && contentType?.includes('application/json')) {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            error.response.data = json;
            message = json.message || defaultMessage;
          } else {
            // Otros errores con estructura JSON directa
            message = error?.response?.data?.message || defaultMessage;
          }
        } catch (e) {
          console.error("Error al procesar el contenido del error:", e);
        }

        if (error?.response?.data?.isBusinessRuleFail) {
          addToast({
            mostrar: true,
            titulo: 'Regla de negocio',
            tipo: error?.response?.data?.errorMessageType,
            mensaje: message,
          });

          return error;
        }


        // Verifica si se debe mostrar el toast (por defecto sí)
        const showToast = error?.config?.headers?.["X-Hide-Toast"] !== "true";

        if (showToast) {
          addToast({
            mostrar: true,
            titulo: "Error",
            tipo: "Danger",
            mensaje: message,
          });
        }

        if (error?.config?.headers?.["X-Hide-Toast"]) {
          delete error.config.headers["X-Hide-Toast"];
        }

        return error;
        //return Promise.reject(error); // <-- importante rechazar para que las promesas que usen .catch lo manejen
      }
    );


    // Limpieza: Eliminar interceptores cuando el componente se desmonta
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [addToast, showLoader, hideLoader, instance]);

  return null;
};

export default axiosInstance;
export { AxiosInterceptor };
